import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@vaiu/ui/card';
import { Label } from '@vaiu/ui/label';
import { Switch } from '@vaiu/ui/switch';
import { Button } from '@vaiu/ui/button';
import { Alert, AlertDescription } from '@vaiu/ui/alert';
import { MessageSquare, Bell, Mail, CheckCircle, AlertCircle } from 'lucide-react';
import { platformApi } from '@/services/platformApi';
import { useToast } from '@/hooks/use-toast';
import { LoadingSpinner } from '@vaiu/ui';
import type { CommunicationPreferences } from '@/types/api/user';

export const CommunicationPreferences: React.FC = () => {
  const { toast } = useToast();
  const [preferences, setPreferences] = useState<CommunicationPreferences>({
    autoSubscribe: true,
    emailNotifications: true,
    pushNotifications: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      setLoading(true);
      const prefs = await platformApi.getCommunicationPreferences();
      setPreferences({
        autoSubscribe: prefs.autoSubscribe ?? true,
        emailNotifications: prefs.emailNotifications ?? true,
        pushNotifications: prefs.pushNotifications ?? true
      });
    } catch (error: any) {
      console.error('Failed to load communication preferences:', error);
      // Use defaults if API fails
      setPreferences({
        autoSubscribe: true,
        emailNotifications: true,
        pushNotifications: true
      });
      toast({
        title: 'Note',
        description: 'Using default preferences. Some features may not be available.',
        variant: 'default'
      });
    } finally {
      setLoading(false);
    }
  };

  const savePreferences = async () => {
    try {
      setSaving(true);
      await platformApi.updateCommunicationPreferences(preferences);
      setHasChanges(false);
      toast({
        title: 'Preferences Saved',
        description: 'Your communication preferences have been updated.',
      });
    } catch (error: any) {
      console.error('Failed to save communication preferences:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save preferences. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = (key: keyof CommunicationPreferences) => {
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
    setHasChanges(true);
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            Communication Preferences
          </CardTitle>
          <CardDescription>Loading your preferences...</CardDescription>
        </CardHeader>
        <CardContent>
          <LoadingSpinner text="Loading preferences..." />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageSquare className="w-5 h-5" />
          Communication Preferences
        </CardTitle>
        <CardDescription>
          Control how you receive messages and notifications from merchants
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Auto-Subscribe Preference */}
        <div className="flex items-start justify-between space-x-4 p-4 border rounded-lg">
          <div className="flex-1 space-y-1">
            <div className="flex items-center gap-2">
              <Label htmlFor="auto-subscribe" className="text-base font-medium cursor-pointer">
                Auto-subscribe to businesses after transaction
              </Label>
            </div>
            <p className="text-sm text-muted-foreground">
              When enabled, you'll automatically follow businesses after making a purchase. 
              You can unsubscribe at any time.
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              <strong>Note:</strong> This preference only applies to future transactions. 
              Existing subscriptions are not affected.
            </p>
          </div>
          <Switch
            id="auto-subscribe"
            checked={preferences.autoSubscribe ?? true}
            onCheckedChange={() => handleToggle('autoSubscribe')}
          />
        </div>

        {/* Email Notifications */}
        <div className="flex items-start justify-between space-x-4 p-4 border rounded-lg">
          <div className="flex-1 space-y-1">
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-muted-foreground" />
              <Label htmlFor="email-notifications" className="text-base font-medium cursor-pointer">
                Email notifications for messages
              </Label>
            </div>
            <p className="text-sm text-muted-foreground">
              Receive email notifications when merchants send you messages or announcements.
            </p>
          </div>
          <Switch
            id="email-notifications"
            checked={preferences.emailNotifications ?? true}
            onCheckedChange={() => handleToggle('emailNotifications')}
          />
        </div>

        {/* Push Notifications */}
        <div className="flex items-start justify-between space-x-4 p-4 border rounded-lg">
          <div className="flex-1 space-y-1">
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4 text-muted-foreground" />
              <Label htmlFor="push-notifications" className="text-base font-medium cursor-pointer">
                Push notifications for messages
              </Label>
            </div>
            <p className="text-sm text-muted-foreground">
              Receive push notifications on your device when merchants send you messages.
            </p>
          </div>
          <Switch
            id="push-notifications"
            checked={preferences.pushNotifications ?? true}
            onCheckedChange={() => handleToggle('pushNotifications')}
          />
        </div>

        {/* Info Alert */}
        <Alert>
          <MessageSquare className="h-4 w-4" />
          <AlertDescription>
            <strong>How it works:</strong> When you pay a merchant, you'll automatically become 
            a follower (if auto-subscribe is enabled). Merchants can then send you announcements 
            and message you about your transactions. You can unsubscribe or block merchants at any time.
          </AlertDescription>
        </Alert>

        {/* Save Button */}
        {hasChanges && (
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => {
                loadPreferences();
                setHasChanges(false);
              }}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              onClick={savePreferences}
              disabled={saving}
              className="min-w-[120px]"
            >
              {saving ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Save Preferences
                </>
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CommunicationPreferences;

